/**
 * Canonical role names, shared by every app.
 *
 * These strings are compared in ~30 backend call sites (permission
 * guard, trial guard, backdate interceptor, subscription interceptor,
 * several repositories) and in ~37 neocrm files. They were raw string
 * literals on the backend and a neocrm-local enum on the frontend —
 * two definitions of the same domain vocabulary, with the backend half
 * typo-prone because nothing typechecked `'super_admin'`.
 *
 * `apps/neocrm/src/app/shared/constants/roles.constant.ts` re-exports
 * this as `Roles`, so existing frontend call sites are unchanged.
 *
 * Const object + type alias (not a TS `enum`) per the repo's
 * "No magic-string comparisons" rule: members are addressable at
 * runtime AND the resulting type is still the same string union, so
 * every legacy call site keeps compiling.
 */
export const RoleName = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  MANAGER: 'manager',
  CASHIER: 'cashier',
} as const;

export type RoleName = (typeof RoleName)[keyof typeof RoleName];

/**
 * Roles that reach every branch of their business implicitly, without
 * any `user_branch_role` grant. See
 * `apps/neoapi/src/common/services/branch-access.service.ts`.
 */
export const BRANCH_UNRESTRICTED_ROLES: readonly RoleName[] = [
  RoleName.ADMIN,
  RoleName.SUPER_ADMIN,
];
